import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Gem, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login, register, resetPassword, testLogin, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else if (mode === "register") await register(email, password, name);
      else if (mode === "reset") {
        await resetPassword(email, password);
        toast.success("Password reset. Sign in with your new password.");
        setPassword("");
        setMode("login");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const demo = async () => {
    setBusy(true);
    try {
      await testLogin();
    } catch {
      toast.error("Demo login unavailable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg)]">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[var(--surface-3)] border-r border-[color:var(--border-subtle)] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/36950633/pexels-photo-36950633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-[12px] bg-[var(--green)] text-[#f7fbf9]">
            <Gem className="h-6 w-6" />
          </span>
          <div className="leading-tight">
            <div className="font-serif-display text-lg text-[var(--text)]">Wealth Command Center</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Private financial cockpit</div>
          </div>
        </div>
        <div className="relative max-w-md">
          <div className="h-[2px] w-16 bg-[var(--gold)] mb-6" />
          <h2 className="font-serif-display text-4xl leading-tight tracking-[-0.02em] text-[var(--text)]">
            Every account, goal, and dollar — composed like a private banker's briefing.
          </h2>
          <p className="mt-4 text-[var(--text-muted)]">
            Track net worth, budget with intention, plan purchases, and get AI-guided insights across your entire financial life.
          </p>
        </div>
        <div className="relative text-xs text-[var(--text-faint)]">Bank-grade calm. Editorial clarity.</div>
      </div>

      {/* Right auth card */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="max-w-md w-full">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <span className="grid place-items-center h-10 w-10 rounded-[10px] bg-[var(--green)] text-[#f7fbf9]">
              <Gem className="h-5 w-5" />
            </span>
            <span className="font-serif-display text-lg text-[var(--text)]">Wealth Command Center</span>
          </div>

          <div className="rounded-[16px] bg-[var(--surface-2)] border border-[color:var(--border-subtle)] shadow-[var(--shadow-md)] p-8">
            <h1 className="font-serif-display text-2xl text-[var(--text)]">
              {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password"}
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              {mode === "login" ? "Sign in to your command center." : mode === "register" ? "Start commanding your wealth today." : "Enter your email and choose a new password."}
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-[var(--text-muted)]">Display name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Vance"
                    className="bg-white border-[color:var(--border-subtle)]" data-testid="auth-name-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-muted)]">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" className="bg-white border-[color:var(--border-subtle)]" data-testid="auth-email-input" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-[var(--text-muted)]">{mode === "reset" ? "New password" : "Password"}</Label>
                  {mode === "login" && (
                    <button type="button" onClick={() => { setMode("reset"); setPassword(""); }}
                      className="text-xs text-[var(--green)] font-medium hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="bg-white border-[color:var(--border-subtle)]" data-testid="auth-password-input" />
              </div>
              <Button type="submit" disabled={busy} data-testid="auth-submit-button"
                className="w-full bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]">
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </form>

            {mode === "reset" ? (
              <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
                Remembered it?{" "}
                <button type="button" onClick={() => setMode("login")}
                  className="text-[var(--green)] font-medium hover:underline" data-testid="auth-back-to-login">
                  Back to sign in
                </button>
              </p>
            ) : (
              <>
                <div className="my-5 flex items-center gap-3 text-xs text-[var(--text-faint)]">
                  <div className="h-px flex-1 bg-[color:var(--border-subtle)]" /> or <div className="h-px flex-1 bg-[color:var(--border-subtle)]" />
                </div>

                <Button onClick={demo} disabled={busy} variant="outline" data-testid="demo-login-button"
                  className="w-full border-[color:var(--border-subtle)] text-[var(--text)] hover:bg-[color:rgba(198,139,60,0.10)]">
                  Explore with demo data
                </Button>

                <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
                  {mode === "login" ? "New here?" : "Already have an account?"}{" "}
                  <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}
                    className="text-[var(--green)] font-medium hover:underline" data-testid="auth-toggle-mode">
                    {mode === "login" ? "Create an account" : "Sign in"}
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
